// Copilot API client for MCP server calls
import { fetchClientConfig } from './auth';

let _mcpApiUrl = process.env.REACT_APP_MCP_API_URL || 'https://core.yantrai.com/mcp/call';
let _configFetched = false;

async function ensureConfig(): Promise<string> {
  if (!_configFetched) {
    try {
      const cfg = await fetchClientConfig();
      if (cfg.mcp_api_url) _mcpApiUrl = cfg.mcp_api_url;
    } catch {
      // fall back to default URL
    }
    _configFetched = true;
  }
  return _mcpApiUrl;
}

// Types
export interface ResearchQuery {
  query: string;
  company_names?: string[];
  top_k?: number;
  deep_search?: boolean;
  return_pdf?: boolean;
  pdf_expiry_hours?: number;
  enable_multi_agent?: boolean;
  language?: string;
  model?: string;
  session_id?: string;
  clarification_response?: string;
}

export interface ClarificationOption {
  label: string;
  value: string;
}

export interface ClarificationInfo {
  type: 'ambiguous_company' | 'vague_query' | 'missing_parameters';
  message: string;
  options: ClarificationOption[];
  allow_freetext: boolean;
  session_id: string;
  original_query?: string;
}

export interface ResearchSource {
  type: 'database' | 'web' | 'company_insight';
  company?: string;
  report_type?: string;
  similarity?: number;
  title?: string;
  url?: string;
  insight_type?: string;
}

export interface EvaluationStep {
  step_index: number;
  agent: string;
  label: string;
  status: 'completed' | 'skipped' | 'error';
  summary: string;
  details: Record<string, any>;
}

export interface ResearchResult {
  status: string;
  pdf_url?: string;
  format?: string;
  query?: string;
  companies_covered?: string;
  total_documents?: number;
  pdf_expiry_hours?: number;
  llm_response?: string;
  multi_agent?: boolean;
  workflow_trace?: string[];
  query_type?: string;
  confidence_score?: number;
  db_search_status?: string;
  web_search_status?: string;
  coordinator_reasoning?: string;
  sources?: ResearchSource[];
  evaluation_trace?: EvaluationStep[];
  llm_response_en?: string;
  response_language?: string;
  error?: string;
  clarification?: ClarificationInfo;
}

export interface CompanyInsights {
  business_overview?: string;
  future_plans?: string;
  product_segments?: string;
  suppliers?: string;
  clientele?: string;
  joint_ventures?: string;
  subsidiaries?: string;
  key_management_people_changes?: string;
  management_guidance?: string;
  related_party_transactions?: string;
}

export interface CompanyDetails {
  name: string;
  ticker_symbol?: string;
  sector?: string;
  industry?: string;
  market_cap?: number;
  current_price?: number;
  price_to_earnings?: number;
  book_value?: number;
  dividend_yield?: number;
  roce?: number;
  roe?: number;
  face_value?: number;
  market_cap_to_sales?: number;
  ev_to_ebitda?: number;
  price_to_book?: number;
  nse_link?: string;
  // Financial data
  quarterly_results_data?: any;
  yearly_results_data?: any;
  balance_sheets?: any;
  cashflow_statements?: any;
  ratios?: any;
  // Documents
  annual_reports_data?: any[];
  concalls_data?: any[];
  company_announcements_data?: any[];
  // Insights from PostgreSQL
  company_insights?: CompanyInsights;
  // Segment data
  product_segments?: Array<Record<string, string>>;
  geographic_segments?: Array<Record<string, string>>;
  // Shareholding
  shareholding_ratios?: Array<Record<string, string>>;
  // Peer comparison
  peer_comparison?: Array<Record<string, string>>;
  // Growth metrics
  sales_growth?: Record<string, string>;
  profit_growth?: Record<string, string>;
  stock_price_cagr?: Record<string, string>;
  roe_growth?: Record<string, string>;
  // Quarterly metrics
  quarterly_metrics?: {
    sales_yoy?: number | null;
    sales_qoq?: number | null;
    np_yoy?: number | null;
    np_qoq?: number | null;
    ebitda_yoy?: number | null;
    ebitda_qoq?: number | null;
    cf_profit_ratio?: number | null;
  };
  // Price range
  high_low?: string;
  // Corporate actions
  corporate_actions?: Array<Record<string, string>>;
  // Analysis
  company_analysis?: { pros?: string[]; cons?: string[] };
  // Computed growth shorthand (from search_companies)
  opm_pct?: number;
  sales_growth_3y?: number;
  profit_growth_3y?: number;
  // Multi-period growth metrics
  growth_metrics?: {
    quarterly: { rev: number | null; pat: number | null; opm: number | null };
    yearly:    { rev: number | null; pat: number | null; opm: number | null };
    '3y':      { rev: number | null; pat: number | null; opm: number | null };
    '5y':      { rev: number | null; pat: number | null; opm: number | null };
  };
  // Corporate Governance
  corporate_governance?: {
    governance_score: number | null;
    score_label: string;
    score_components: Record<string, number | null>;
    red_flags: string[];
    green_flags: string[];
    promoter_holding_trend: Array<{ period: string; percentage: number }>;
    institutional_holding_trend: Array<{ period: string; fii: number; dii: number }>;
    debt_to_equity: number | null;
    interest_coverage: number | null;
  };
  [key: string]: any;
}

export interface CompanySearchResult {
  status: string;
  data: CompanyDetails[];
  pagination?: {
    page: number;
    page_size: number;
    has_next: boolean;
  };
  query?: string;
  error?: string;
}

// LLM model options for copilot dropdown
export interface CopilotModelOption {
  key: string;
  label: string;
  description: string;
}

export async function fetchCopilotModels(): Promise<CopilotModelOption[]> {
  const apiUrl = await ensureConfig();
  const base = apiUrl.replace('/call', '');
  try {
    const res = await fetch(`${base}/api/llm-models`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : (data.models || []);
  } catch {
    return [{ key: 'fast', label: 'Fast', description: 'Quick responses' }];
  }
}

// Base MCP call function
async function callMCP<T>(toolName: string, args: Record<string, any>, signal?: AbortSignal): Promise<T> {
  const apiUrl = await ensureConfig();
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: toolName,
      arguments: args,
    }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return await response.json();
}

// Query stock research
export async function queryStockResearch(params: ResearchQuery, signal?: AbortSignal): Promise<ResearchResult> {
  const args: Record<string, any> = {
    query: params.query,
    company_names: params.company_names,
    top_k: params.top_k || 5,
    deep_search: params.deep_search || false,
    return_pdf: params.return_pdf ?? true,
    pdf_expiry_hours: params.pdf_expiry_hours || 24,
    enable_multi_agent: params.enable_multi_agent || false,
    language: params.language || 'en-IN',
    model: params.model || 'fast',
  };
  if (params.session_id) args.session_id = params.session_id;
  if (params.clarification_response) args.clarification_response = params.clarification_response;
  return callMCP<ResearchResult>('query_stock_research', args, signal);
}

// Get all company names for autocomplete
export async function getAllCompanyNames(): Promise<string[]> {
  const result = await callMCP<{ status: string; data: string[] }>('get_all_companies', {
    page: 1,
    page_size: 10000,
  });

  if (result.status === 'success' && result.data) {
    return result.data.sort();
  }
  return [];
}

// Search companies with filters and pagination
export async function searchCompanies(
  criteria: string,
  page: number = 1,
  pageSize: number = 20
): Promise<CompanySearchResult> {
  return callMCP<CompanySearchResult>('search_companies', {
    criteria: criteria,
    page: page,
    page_size: pageSize,
    sort_by: 'name',
    order: 'asc',
  });
}

// Get filter options (sectors, industries, sector-to-industry mapping)
export interface FilterOptions {
  sectors: string[];
  industries: string[];
  sector_industry_map: Record<string, string[]>;
}

export async function getFilterOptions(sector?: string): Promise<FilterOptions> {
  const args: Record<string, any> = {};
  if (sector) args.sector = sector;
  const result = await callMCP<{ status: string; data: FilterOptions }>('get_filter_options', args);
  if (result.status === 'success' && result.data) return result.data;
  return { sectors: [], industries: [], sector_industry_map: {} };
}

// Get company details — try company_name first, fall back to ticker_symbol
export async function getCompanyDetails(companyName: string): Promise<{ status: string; data?: CompanyDetails; error?: string }> {
  const result = await callMCP<{ status: string; data?: CompanyDetails; error?: string }>('get_company_details', {
    company_name: companyName,
  });
  if (result.status === 'success' && result.data) return result;
  // Retry as ticker symbol (handles routes like /company/INFY)
  return callMCP<{ status: string; data?: CompanyDetails; error?: string }>('get_company_details', {
    ticker_symbol: companyName,
  });
}

// Sector overview — server-side aggregation
export interface SectorOverview {
  name: string;
  companyCount: number;
  totalMcap: number;
  avgPE: number;
  avgROE: number;
  avgROCE: number;
  avgMcapSales: number;
  avgPB: number;
  avgEvEbitda: number;
  totalRevenue: number;
  totalProfit: number;
  avgOPM: number;
}

export async function getSectorOverview(): Promise<SectorOverview[]> {
  const result = await callMCP<{ status: string; data: SectorOverview[] }>('get_sector_overview', {});
  return result.status === 'success' && result.data ? result.data : [];
}

// Historical sector metrics for charts
export interface SectorMetricPoint {
  date: string;
  avgPE: number;
  avgPB: number;
  avgMcapSales: number;
  avgEvEbitda: number;
  avgROE: number;
  avgROCE: number;
  totalMcap: number;
  totalRevenue: number;
  totalProfit: number;
  avgOPM: number;
}

export async function getSectorMetricsHistory(
  sector?: string
): Promise<Record<string, SectorMetricPoint[]>> {
  const args: Record<string, any> = {};
  if (sector) args.sector = sector;
  const result = await callMCP<{ status: string; data: Record<string, SectorMetricPoint[]> }>(
    'get_sector_metrics_history', args
  );
  return result.status === 'success' && result.data ? result.data : {};
}

// Historical company valuation ratios
export interface CompanyRatioPoint {
  date: string;
  pe?: number;
  pb?: number;
  mcapSales?: number;
  evEbitda?: number;
}

export async function getCompanyRatiosHistory(tickerSymbol: string): Promise<CompanyRatioPoint[]> {
  const result = await callMCP<{ status: string; data?: { data_points: CompanyRatioPoint[] } }>(
    'get_company_ratios_history', { ticker_symbol: tickerSymbol }
  );
  return result.status === 'success' && result.data?.data_points ? result.data.data_points : [];
}

// Price history
export interface PricePoint {
  date: string;
  close: number;
  volume: number;
}

export async function getPriceHistory(
  tickerSymbol: string,
  fromDate?: string,
  toDate?: string
): Promise<PricePoint[]> {
  const args: Record<string, any> = { ticker_symbol: tickerSymbol };
  if (fromDate) args.from_date = fromDate;
  if (toDate) args.to_date = toDate;
  const result = await callMCP<{ status: string; data?: { prices: PricePoint[] } }>(
    'get_price_history', args
  );
  return result.status === 'success' && result.data?.prices ? result.data.prices : [];
}

// Search companies by sector (for sector dashboard)
export async function searchCompaniesBySector(
  sector: string,
  page: number = 1,
  pageSize: number = 50
): Promise<CompanySearchResult> {
  return callMCP<CompanySearchResult>('search_companies', {
    criteria: `sector = '${sector}'`,
    page,
    page_size: pageSize,
    sort_by: 'market_cap',
    order: 'desc',
  });
}
